import { Link } from '@tanstack/react-router';
import { BellRing, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { notificationPermission, pushSupport } from '../lib/push-browser';

const DISMISSED_KEY = 'nucleus:push-hint-dismissed';

function dismissedBefore(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

/** Shown in the inbox until this browser has been asked about notifications or the hint is closed. */
export function PushInboxHint({ className }: { className?: string }) {
  const { t } = useTranslation('push');
  const [hidden, setHidden] = useState(
    () =>
      dismissedBefore() || pushSupport() !== 'supported' || notificationPermission() !== 'default',
  );
  if (hidden) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Private mode: the hint simply comes back next time.
    }
    setHidden(true);
  };

  return (
    <Alert className={cn('pr-12', className)} data-testid="push-inbox-hint">
      <BellRing aria-hidden />
      <AlertTitle>{t('hint.title')}</AlertTitle>
      <AlertDescription className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span>{t('hint.body')}</span>
        <Link to="/account/notifications" className="text-primary font-medium hover:underline">
          {t('hint.action')}
        </Link>
      </AlertDescription>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 size-7"
        onClick={dismiss}
        aria-label={t('hint.dismiss')}
      >
        <X aria-hidden />
      </Button>
    </Alert>
  );
}
